import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import YoutubeIframe from './YoutubeIframe';
import { getWorldCupVideos, getWorldCupWinnerCount } from '../utils/firestoreApi';
// import shortsData from '../data/shortslist.json'; // 정적 JSON import 제거

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  height: 80vh;
  box-sizing: border-box;
`;

const ContentBox = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: 100%;
  }
`;  

const Title = styled.h1`
  margin-top: 0;
  font-size: 2rem;
  margin-bottom: 16px;
  text-align: center;
  color: white;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin: 0;
  }
`;

const Subtitle = styled.p`
  font-size: 1rem;
  margin-top: 0;
  margin-bottom: 16px;
  text-align: center;
  color: #ccc;
  text-shadow: 1px 1px 2px rgba(0,0,0,0.5);
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const GameContainer = styled.div`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  width: 100%;
`;

const GridContainer = styled.div`
  display: flex;
  flex: 1;
  justify-content: center;
  width: 100%;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  width: 100%;
  max-width: 1200px;
  
  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const GridItem = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const ShortCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 15px;
  padding: 8px;
  width: 100%;
  height: 60vh;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  backdrop-filter: blur(10px);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  
  &:hover {
    border-color: rgba(255, 255, 255, 0.4);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }
  
  @media (max-width: 768px) {
    height: 50vh;
    width: 170px;
  }
`;

const ShortTitle = styled.h2`
  font-size: 1.2rem;
  margin-bottom: 16px;
  text-align: center;
  color: white;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;



const VideoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  border-radius: 4px;
  margin-bottom: 16px;
  margin: 0 auto;
  aspect-ratio: 9/16;
`;

const ButtonContainer = styled.div`
  margin-top: 8px;
  display: flex;
  justify-content: center;
  width: 100%;
`;

const ThumbButton = styled(motion.button)`
  background: ${props => {
    if (props.$disabled) return 'linear-gradient(135deg, #666666, #888888)';
    return 'linear-gradient(135deg, #ff6b6b, #ff8e8e)';
  }};
  color: white;
  border: none;
  border-radius: 50%;
  width: 70px;
  height: 70px;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${props => props.$disabled ? 0.5 : 1};
  box-shadow: ${props => {
    if (props.$disabled) return '0 4px 15px rgba(102, 102, 102, 0.3)';
    return '0 6px 20px rgba(255, 107, 107, 0.4)';
  }};
  position: relative;
  overflow: visible;

  @media (max-width: 768px) {
    width: 50px;
    height: 50px;
  }
  
  &:hover {
    background: ${props => {
      if (props.$disabled) return 'linear-gradient(135deg, #666666, #888888)';
      return 'linear-gradient(135deg, #ff5252, #ff7676)';
    }};
    box-shadow: ${props => {
      if (props.$disabled) return '0 4px 15px rgba(102, 102, 102, 0.3)';
      return '0 8px 25px rgba(255, 107, 107, 0.6)';
    }};
  }
  
  &:disabled {
    cursor: not-allowed;
  }
`;

const MessageTooltip = styled.div`
  position: absolute;
  top: -40px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 107, 107, 0.9);
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  white-space: nowrap;
  opacity: ${props => props.$show ? 1 : 0};
  visibility: ${props => props.$show ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  z-index: 1000;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  pointer-events: none;
  
  &::before {
    content: '';
    position: absolute;
    bottom: -6px;
    left: 50%;
    transform: translateX(-50%);
    border-left: 6px solid transparent;
    border-right: 6px solid transparent;
    border-top: 6px solid rgba(255, 107, 107, 0.9);
  }
  
  @media (max-width: 768px) {
    font-size: 10px;
    padding: 6px 10px;
    top: -35px;
  }
`;

const ThumbIcon = styled.span`
  font-size: 16px;
  line-height: 1;
  z-index: 1;
  transition: transform 0.2s ease;
  font-weight: bold;
  
  &::before {
    content: "선택";
  }

  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

// 결과 화면 스타일
const ResultContainer = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  padding: 15px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 15px;
  height: calc(var(--vh, 1vh) * 100 - 100px);
  box-sizing: border-box;
  justify-content: flex-start;

  @media (max-width: 768px) {
    max-width: 100vw;
    gap: 2px;
    padding: 10px;
  }
`;

const ResultTitle = styled(motion.h1)`
  font-size: 1.8rem;
  margin-bottom: 8px;
  margin-top: 0;
  text-align: center;
  color: white;
  text-shadow: 2px 2px 4px rgba(0,0,0,0.5);

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 6px;
    margin-top: 0;
  }
`;

const WinnerTitle = styled(motion.h2)`
  font-size: 0.9rem;
  margin-bottom: 8px;
  text-align: center;
  color: white;

  @media (max-width: 768px) {
    font-size: 0.7rem;
    margin-bottom: 6px;
  }
`;

const WinnerCount = styled(motion.div)`
  font-size: 0.8rem;
  margin-bottom: 16px;
  text-align: center;
  color: #ffd700;
  background: rgba(255, 215, 0, 0.1);
  padding: 8px 16px;
  border-radius: 20px;
  border: 1px solid rgba(255, 215, 0, 0.3);
  
  @media (max-width: 768px) {
    font-size: 0.6rem;
    padding: 6px 12px;
    margin-bottom: 12px;
  }
`;

const WinnerVideoContainer = styled(motion.div)`
  width: 100%;
  max-width: 800px;
  height: 50vh;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  border-radius: 8px;

  @media (max-width: 768px) {
    aspect-ratio: 9/16;
    width: auto;
    height: 45vh;
  }
`;

const ButtonGroup = styled(motion.div)`
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
  flex-shrink: 0;
  
  @media (max-width: 768px) {
    gap: 8px;
    margin-top: 15px;
  }
`;

const Button = styled.button`
  padding: 12px 24px;
  border: none;
  border-radius: 25px;
  font-size: 1rem;
  font-weight: bold;
  cursor: pointer;
  transition: all 0.3s ease;
  
  @media (max-width: 768px) {
    padding: 8px 16px;
    font-size: 0.8rem;
    border-radius: 20px;
  }
  
  ${props => props.$primary ? `
    background: var(--primary-gradient);
    color: white;
    box-shadow: 0 4px 15px rgba(255, 107, 107, 0.4);
    
    &:hover {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(255, 107, 107, 0.6);
    }
  ` : `
    background: rgba(255, 255, 255, 0.2);
    color: white;
    border: 2px solid rgba(255, 255, 255, 0.3);
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
      transform: translateY(-2px);
    }
  `}
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 80vh;
  color: white;
  font-size: 1.2rem;
`;

const LoadingSpinner = styled.div`
  border: 3px solid rgba(255, 255, 255, 0.3);
  border-top: 3px solid white;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: spin 1s linear infinite;
  margin-right: 15px;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const WorldCup = ({ onBackToHome, onComplete }) => {
  const [shorts, setShorts] = useState([]);
  const [currentRound, setCurrentRound] = useState(1);
  const [currentPlayer, setCurrentPlayer] = useState(0);
  const [winners, setWinners] = useState([]);
  const [currentPair, setCurrentPair] = useState([]);
  const [selectedShorts, setSelectedShorts] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [winner, setWinner] = useState(null);
  // 영상 재생 완료 상태 추가
  const [videosCompleted, setVideosCompleted] = useState({});
  // 로딩 상태 추가
  const [isLoading, setIsLoading] = useState(true);
  // 선택 애니메이션 상태 추가
  const [isSelecting, setIsSelecting] = useState(false);
  // 비활성화된 버튼 클릭 메시지 상태 추가
  const [showDisabledMessage, setShowDisabledMessage] = useState({});
  // 우승자 수 상태 추가
  const [winnerCount, setWinnerCount] = useState(0);
  // 카드 탭 상태 관리 (한 번에 하나의 카드만 확대)
  const [tappedCardId, setTappedCardId] = useState(null);


  useEffect(() => {
    // Firestore에서 월드컵 영상 정보 가져오기
    const loadWorldCupVideos = async () => {
      try {
        setIsLoading(true);
        const videosData = await getWorldCupVideos();
        
        if (videosData && videosData.length > 0) {
          // 싸이퍼 영상과 일반 영상 분리
          const cypherVideos = videosData.filter(video => video.isCypher);
          const normalVideos = videosData.filter(video => !video.isCypher);
          
          // 일반 영상 14개를 랜덤하게 섞기 (Fisher-Yates 셔플 알고리즘 사용)
          const shuffledNormalVideos = [...normalVideos];
          for (let i = shuffledNormalVideos.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffledNormalVideos[i], shuffledNormalVideos[j]] = [shuffledNormalVideos[j], shuffledNormalVideos[i]];
          }
          
          // 16강 구성: 일반 영상 14개 + 싸이퍼 영상 2개 (마지막에 추가)
          const finalShorts = [...shuffledNormalVideos, ...cypherVideos];

          // 결과 테스트용 
          //const finalShorts = shuffledNormalVideos.slice(0, 2);
          
          setShorts(finalShorts);
          setCurrentPlayer(finalShorts.length); // 실제 영상 개수로 설정
          setCurrentPair([finalShorts[0], finalShorts[1]]);
        } else {
          //console.error('월드컵 영상 정보를 찾을 수 없습니다.');
        }
      } catch (error) {
        //console.error('월드컵 영상 정보 로드 실패:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadWorldCupVideos();
    // 새로운 라운드 시작시 영상 완료 상태 초기화
    setVideosCompleted({});
  }, []);

  // 우승자가 결정되었을 때 우승자 수 조회
  useEffect(() => {
    if (winner && showResult) {
      const fetchWinnerCount = async () => {
        try {
          const count = await getWorldCupWinnerCount(winner.videoId);
          setWinnerCount(count);
        } catch (error) {
          //console.error('우승자 수 조회 실패:', error);
          setWinnerCount(0);
        }
      };
      
      fetchWinnerCount();
    }
  }, [winner, showResult]);

  // 영상 재생 완료 핸들러
  const handleVideoEnd = (videoId) => {
    //console.log(`[WorldCup] 영상 완료: ${videoId}`);
    setVideosCompleted(prev => {
      const newState = {
        ...prev,
        [videoId]: true
      };
      //console.log(`[WorldCup] 완료 상태 업데이트:`, newState);
      return newState;
    });
  };

  // 영상 5초 이상 재생 핸들러
  const handleVideoReady = (videoId) => {
    //console.log(`[WorldCup] 영상 5초 이상 재생: ${videoId}`);
    setVideosCompleted(prev => {
      const newState = {
        ...prev,
        [videoId]: true
      };
      //console.log(`[WorldCup] 준비 완료 상태 업데이트:`, newState);
      return newState;
    });
  };

  // 현재 쌍의 두 영상이 모두 재생 완료되었는지 확인 (useMemo로 최적화)
  const areBothVideosCompleted = useMemo(() => {
    if (!currentPair || currentPair.length < 2) return false;
    
    const video1Completed = videosCompleted[currentPair[0]?.id];
    const video2Completed = videosCompleted[currentPair[1]?.id];
    
    // console.log(`[WorldCup] 완료 상태 체크:`, {
    //   video1Id: currentPair[0]?.id,
    //   video1Completed,
    //   video2Id: currentPair[1]?.id,
    //   video2Completed,
    //   allVideosCompleted: videosCompleted
    // });
    
    // 3. 두 영상이 모두 현재 재생시간이 5초 이상이면 버튼 활성화한다
    const bothCompleted = video1Completed && video2Completed;
    
    // if (bothCompleted) {
    //   console.log(`[WorldCup] ✅ 두 영상 모두 5초 완료! 버튼 활성화`);
    // } else {
    //   console.log(`[WorldCup] ⏳ 아직 완료되지 않음 - 영상1: ${video1Completed ? '완료' : '미완료'}, 영상2: ${video2Completed ? '완료' : '미완료'}`);
    // }
    
    return bothCompleted;
    //// 테스트용: 항상 true 반환
    //return true;
  }, [currentPair, videosCompleted]);

  // 카드 탭 핸들러
  const handleCardTap = (cardId) => {
    setTappedCardId(prev => prev === cardId ? null : cardId);
  };

  // 포커스 벗어날 때 핸들러
  const handleCardBlur = () => {
    setTappedCardId(null);
  };

  // 비활성화된 버튼 클릭 핸들러
  const handleDisabledClick = (buttonId) => {
    setShowDisabledMessage(prev => ({
      ...prev,
      [buttonId]: true
    }));
    setTimeout(() => {
      setShowDisabledMessage(prev => ({
        ...prev,
        [buttonId]: false
      }));
    }, 5000); // 5초 후 메시지 숨김
  };

  const handleSelect = (selectedShort) => {
    // 선택 애니메이션 시작
    setIsSelecting(true);
    
    // 선택된 숏츠 상태 업데이트
    setSelectedShorts(prev => ({
      ...prev,
      [selectedShort.id]: true
    }));

    const newWinners = [...winners, selectedShort];
    setWinners(newWinners);

    // 애니메이션 완료 후 다음 단계로 진행
    setTimeout(() => {
      setIsSelecting(false);
      
      // 현재 라운드의 모든 대결이 끝났는지 확인
      if (newWinners.length === shorts.length / 2) {
        if (newWinners.length === 1) {
          // 결승전에서 우승자가 결정된 경우
          setWinner(selectedShort);
          setShowResult(true);
        } else {
          // 다음 라운드로 진행
          setShorts(newWinners);
          setWinners([]);
          setCurrentRound(currentRound + 1);
          setCurrentPlayer(newWinners.length); // 실제 남은 영상 개수로 설정
          setCurrentPair([newWinners[0], newWinners[1]]);
          // 선택 상태와 영상 완료 상태는 다음 라운드가 렌더링된 후 초기화
          setTimeout(() => {
            setSelectedShorts({});
            setVideosCompleted({});
          }, 100);
        }
      } else {
        // 현재 라운드의 다음 대결로 진행
        const nextIndex = newWinners.length * 2;
        setCurrentPair([shorts[nextIndex], shorts[nextIndex + 1]]);
        // 새로운 쌍의 영상 완료 상태는 다음 쌍이 렌더링된 후 초기화
        setTimeout(() => {
          setVideosCompleted({});
        }, 100);
      }
    }, 300); // 애니메이션 지속 시간
  };

  const handleRestart = async () => {
    try {
      setIsLoading(true);
      const videosData = await getWorldCupVideos();
      
      if (videosData && videosData.length > 0) {
        // 싸이퍼 영상과 일반 영상 분리
        const cypherVideos = videosData.filter(video => video.isCypher);
        const normalVideos = videosData.filter(video => !video.isCypher);
        
        // 일반 영상 14개를 랜덤하게 섞기 (Fisher-Yates 셔플 알고리즘 사용)
        const shuffledNormalVideos = [...normalVideos];
        for (let i = shuffledNormalVideos.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [shuffledNormalVideos[i], shuffledNormalVideos[j]] = [shuffledNormalVideos[j], shuffledNormalVideos[i]];
        }
        // 16강 구성: 일반 영상 14개 + 싸이퍼 영상 2개 (마지막에 추가)
        //const finalShorts = [...shuffledNormalVideos, ...cypherVideos];
        // 테스트용 2개 구성
        const finalShorts = shuffledNormalVideos.slice(0, 2);
        
        setShorts(finalShorts);
        setCurrentRound(1);
        setCurrentPlayer(finalShorts.length); // 실제 영상 개수로 설정
        setWinners([]);
        setCurrentPair([finalShorts[0], finalShorts[1]]);
        setSelectedShorts({});
        setVideosCompleted({});
        setShowResult(false);
        setWinner(null);
      }
    } catch (error) {
      //console.error('월드컵 영상 정보 로드 실패:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoHome = () => {
    if (onBackToHome) {
      onBackToHome();
    }
  };

  const handleSubmit = () => {
    // YouTube URL로 외부 이동
    const youtubeUrl = `https://www.youtube.com/watch?v=${winner.videoId}`;
    window.open(youtubeUrl, '_blank');
    
    // 게임 결과를 onComplete로 전달하여 게임 결과 화면으로 이동
    if (onComplete) {
      onComplete({
        title: winner.title,
        finalVideoId: winner.videoId
      });
    }
  };

  // 로딩 중일 때 표시
  if (isLoading) {
    return (
      <LoadingContainer>
        <LoadingSpinner />
        월드컵 영상을 불러오는 중...
      </LoadingContainer>
    );
  }
  
  if (showResult && winner) {
    return (
      <ResultContainer
        initial={{ opacity: 0, scale: 0.8, y: 50 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ 
          duration: 0.8,
          ease: "easeOut",
          staggerChildren: 0.2
        }}
      >
        <ResultTitle
          initial={{ opacity: 0, y: -30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
        >
          {winner.title}
        </ResultTitle>
        {/* <WinnerTitle
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          
        </WinnerTitle> */}
        <WinnerCount
          initial={{ opacity: 0, y: -15 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.6 }}
        >
          🏆 {winnerCount}명이 선택한 영상입니다
        </WinnerCount>
        <WinnerVideoContainer
          initial={{ opacity: 0, scale: 0.9, y: 30 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <YoutubeIframe 
            videoId={winner.videoId}
            autoplay={1}
            mute={1}
            loop={1}
          />
        </WinnerVideoContainer>
        

        
        <ButtonGroup
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 1.0 }}
        >
          <Button onClick={handleRestart}>다시 시작하기</Button>
          <Button $primary onClick={handleSubmit}>댓글 달고 확률 UP!</Button>
          {/* <Button onClick={handleGoHome}>돌아가기</Button> */}
        </ButtonGroup>
      </ResultContainer>
    );
  }

  return (
    <Container>
      <ContentBox>
        <Title>
          {currentPlayer === 2 ? '결승' : `${currentPlayer}강`}
        </Title>
        <Subtitle>
          두 영상을 모두 시청 후 마음에 드는 영상을 선택해주세요!
        </Subtitle>
        <GameContainer>
          <GridContainer>
            <Grid>
              <AnimatePresence mode="wait">
                {currentPair.map((short) => (
                  <GridItem key={short.id}>
                    <ShortCard
                      initial={{ opacity: 0, scale: 0.8 }}
                                             animate={{ 
                         opacity: isSelecting && !selectedShorts[short.id] ? 0 : 1,
                         scale: selectedShorts[short.id] ? 1.05 : (tappedCardId === short.id ? 1.2 : 1),
                         boxShadow: selectedShorts[short.id] 
                           ? "0 10px 30px rgba(0,0,0,0.3)" 
                           : "0 2px 10px rgba(0,0,0,0.1)"
                       }}
                      exit={{ opacity: 0, scale: 0.8 }}
                                             whileHover={{ 
                         scale: tappedCardId === short.id ? 1.2 : 1.1,
                         transition: { duration: 0.2, ease: "easeOut" }
                       }}
                        onTap={() => handleCardTap(short.id)}
                        onBlur={handleCardBlur}
                        tabIndex={0}
                      
                      transition={{ 
                        duration: 0.3,
                        ease: "easeInOut"
                      }}
                    >
                      {/* <ShortTitle>{short.title}</ShortTitle> */}
                      <VideoContainer>
                        <YoutubeIframe 
                          videoId={short.videoId}
                          autoplay={0}
                          mute={0}
                          loop={0}
                          shortId={short.id}
                          onVideoEnd={() => handleVideoEnd(short.id)}
                          onVideoReady={() => handleVideoReady(short.id)}
                        />

                      </VideoContainer>
                      <ButtonContainer>
                        <ThumbButton
                          onClick={() => {
                            if (!areBothVideosCompleted || selectedShorts[short.id]) {
                              handleDisabledClick(short.id);
                            } else {
                              handleSelect(short);
                            }
                          }}
                          $disabled={!areBothVideosCompleted || selectedShorts[short.id]}
                          selected={selectedShorts[short.id]}
                          whileHover={{ 
                            boxShadow: "0 10px 30px rgba(255, 107, 107, 0.8)"
                          }}
                          whileTap={{ 
                            scale: 0.9,
                            rotate: -5
                          }}
                          initial={{ scale: 0.8, opacity: 0 }}
                          animate={{ 
                            opacity: 1,
                            background: selectedShorts[short.id] 
                              ? "linear-gradient(135deg, #ff1744, #ff4081)" 
                              : undefined
                          }}
                          transition={{ 
                            duration: 0.4,
                            ease: "easeOut"
                          }}
                        >
                          <ThumbIcon />
                          <MessageTooltip $show={showDisabledMessage[short.id]}>
                            영상을 모두 시청해주세요!
                          </MessageTooltip>
                        </ThumbButton>
                      </ButtonContainer>
                    </ShortCard>
                  </GridItem>
                ))}
              </AnimatePresence>
            </Grid>
          </GridContainer>
        </GameContainer>
      </ContentBox>
    </Container>
  );
};

export default WorldCup; 